/**
 * The mark at the left of an application or dashboard header.
 *
 * Shared by AppHeader and the dashboard viewer so a logo looks the same
 * wherever it is shown. The two headers are otherwise separate components with
 * separate themes, and a logo copied into both would be the kind of duplicate
 * that quietly drifts.
 *
 * Falls back to the built-in compass whenever there is no custom logo — and
 * also when a custom one fails to load, which is the case that matters: a
 * pasted URL can rot, and a header with a broken-image icon in it looks like a
 * bug in the portal rather than a bad link in someone's config.
 */

import { useEffect, useState } from 'react';
import { Compass } from 'lucide-react';

/** Height of the mark, in px, when the theme does not say. */
export const DEFAULT_LOGO_SIZE = 28;

export default function HeaderLogo({
  url,
  size = DEFAULT_LOGO_SIZE,
}: {
  /** Data URI or absolute URL. Absent = the built-in compass mark. */
  url?: string;
  /** Rendered height in px; width follows the image's aspect ratio. */
  size?: number;
}) {
  const [broken, setBroken] = useState(false);

  // A new logo deserves a fresh attempt — without this, one bad URL would keep
  // the fallback showing even after the admin picked a good image.
  useEffect(() => setBroken(false), [url]);

  if (url && !broken) {
    return (
      <img
        src={url}
        /* Decorative: the application's name is right beside it, so a screen
           reader announcing the logo too would only repeat itself. */
        alt=""
        onError={() => setBroken(true)}
        className="flex-shrink-0 object-contain"
        style={{
          height: size,
          // A wordmark is far wider than it is tall. Capped in proportion to
          // the chosen height so a wide logo cannot crowd out the title.
          maxWidth: size * 6,
        }}
      />
    );
  }

  return (
    <div
      className="flex flex-shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-blue-500 to-blue-700"
      style={{ height: size, width: size }}
    >
      <Compass className="text-white" style={{ height: size * 0.57, width: size * 0.57 }} />
    </div>
  );
}
